import { listScheduleEventsPage } from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  buildCompetitionEventsQuery,
  type BuildCompetitionEventsQueryArgs,
} from '../buildCompetitionEventsQuery'
import { EVENTS_PER_PAGE_STREAM } from '../constants'
import type { ScheduleOrder } from '../filterEvents'
import { useEventStream, type EventStream } from './useEventStream'

export interface UseCompetitionEventStreamArgs
  extends BuildCompetitionEventsQueryArgs {
  /**
   * The schedule to list: the selected division, else the selected season, else
   * the competition the page is for. The caller resolves that precedence — it
   * already tracks it to point the games list at the same schedule, and
   * duplicating it here would let the two lists disagree.
   */
  scheduleId?: string | null
  /** False while the schedule filters are still resolving; keeps it idle. */
  enabled?: boolean
}

/**
 * A COMPETITION's events, paginated, for the merged schedule list.
 *
 * The stream counterpart of `useCompetitionEvents`, which drains every page.
 * Same endpoint, same filters, same schedule-is-the-path arrangement — it pages
 * as the user scrolls instead of arriving complete.
 *
 * Kept separate from `useScheduleEventStream` for the reason the fetch-all pair
 * are separate: a competition page has no team of its own, so it scopes by the
 * schedule and treats team as a FILTER. See `buildCompetitionEventsQuery`.
 *
 * The page size drops from the fetch-all hook's 200 to `EVENTS_PER_PAGE_STREAM`
 * — 200 was sized to drain a schedule in one trip, which is the opposite of
 * what a stream wants.
 */
export const useCompetitionEventStream = ({
  scheduleId,
  enabled = true,
  ...args
}: UseCompetitionEventStreamArgs): EventStream => {
  'use memo'

  const order = (args.order as ScheduleOrder) ?? 'asc'
  const query = {
    ...buildCompetitionEventsQuery(args),
    per_page: EVENTS_PER_PAGE_STREAM,
  }

  // The schedule is the request PATH, so without one there is nothing to ask.
  const isEnabled = enabled && Boolean(scheduleId)

  const stream = useEventStream({
    queryKey: queryKeys.events.byScheduleStream(scheduleId, query),
    fetchPage: (page) =>
      listScheduleEventsPage(scheduleId as string, query, page),
    enabled: isEnabled,
    order,
  })

  return {
    ...stream,
    // While the filters are still resolving the caller IS waiting, so report
    // loading rather than letting the list settle empty and flash its empty
    // state.
    isLoading: enabled ? stream.isLoading : true,
  }
}
